/**
 * Cost Projection Card - Displays projected monthly/yearly costs.
 */

import type { CostProjection } from '../../types';

interface CostProjectionCardProps {
  data: CostProjection | undefined;
  isLoading: boolean;
  error: Error | null;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function CostProjectionCard({ data, isLoading, error }: CostProjectionCardProps) {
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">Failed to load projections: {error.message}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Cost Projections</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Cost Projections</h3>
        <span className="text-xs text-gray-500">
          Based on {data.projection_based_on_days} days of data
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-blue-50 p-4">
          <p className="text-sm font-medium text-gray-500">Monthly Projection</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {formatCurrency(data.monthly_projected_cost)}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            ~{formatCurrency(data.cost_per_company_per_month)} per company
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-purple-50 p-4">
          <p className="text-sm font-medium text-gray-500">Annual Projection</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {formatCurrency(data.yearly_projected_cost)}
          </p>
          <p className="mt-1 text-xs text-gray-400">At current usage rate</p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Usage Metrics</h4>
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-2xl font-semibold text-gray-900">
              {data.avg_builds_per_company.toFixed(1)}
            </p>
            <p className="text-xs text-gray-500">Avg builds per company</p>
          </div>
          <div>
            <p className="text-2xl font-semibold text-gray-900">
              {data.avg_refreshes_per_company.toFixed(1)}
            </p>
            <p className="text-xs text-gray-500">Avg refreshes per company</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CostProjectionCard;
